import { PowerTableUI } from "./PowertableUI";
import { DownloadCsv } from "./DownloadCsv";
import { DragResizeModal } from "../ContextMenu/Modals/DragResizeModal";
import debug from "debug";
const log = debug("app:pwr");

/**
 * A class for managing the Power Table, its UI, data updates, and interactions.
 * @category Components
 * @subcategory PowerTable
 */
export class PowerTable {
  /**
   * Initializes a new instance of the PowerTable class.
   * @param {Object} experience - The experience object containing application-level data and services.
   */
  constructor(experience) {
    /**  @param {Object} The event hub for managing global application events. */
    this.eventHub = experience.eventHub;

    /** @param {DownloadCsv} Instance for handling CSV downloads. */
    this.csvDownloader = new DownloadCsv();

    /** @param {PowerTableUI} Instance for managing Power Table UI. */
    this.powerTableUI = new PowerTableUI(this.csvDownloader);

    /** @param {HTMLElement} The modal container element for the Power Table. */
    this.modalDiv = this.powerTableUI.createModal();

    document.body.appendChild(this.modalDiv);

    /** @param {DragResizeModal|null} Handles drag and resize functionality for the modal. */
    this.dragResizeModal = null;

    this.setupEventListeners();
    this.initializeUI();
  }

  /**
   * Sets up event listeners for handling Power Table-related events.
   */
  setupEventListeners() {
    this.eventHub.on("pwr-switch", this.handlePowerSwitchEvent.bind(this)); // Listen for pwr-switch events
    this.eventHub.on("powerUpdated", this.updatePowerTable.bind(this));
  }

  cleanup() {
    console.log("PowerTable cleanup started...");
  
    // 1. Remove event listeners from eventHub
    if (this.eventHub) {
      this.eventHub.off("pwr-switch", this.handlePowerSwitchEvent);
      this.eventHub.off("powerUpdated", this.updatePowerTable);
    }
  
    // 2. Dispose of DragResizeModal
    if (this.dragResizeModal && typeof this.dragResizeModal.dispose === "function") {
      this.dragResizeModal.dispose();
      this.dragResizeModal = null;
    }
  
    // 3. Dispose of PowerTableUI (if applicable)
    if (this.powerTableUI && typeof this.powerTableUI.cleanup === "function") {
      this.powerTableUI.cleanup();
      this.powerTableUI = null;
    }
  
    // 4. Dispose of CSV Downloader
    if (this.csvDownloader && typeof this.csvDownloader.cleanup === "function") {
      this.csvDownloader.cleanup();
      this.csvDownloader = null;
    }
  
    // 5. Remove modal from DOM if it exists
    if (this.modalDiv && this.modalDiv.parentNode) {
      this.modalDiv.parentNode.removeChild(this.modalDiv);
      this.modalDiv = null;
    }
  
    // 6. Clear object references
    this.eventHub = null;
  
    console.log("PowerTable cleanup completed.");
  }
  /**
   * Initializes the UI by hiding the download button and showing an alert.
   */
  initializeUI() {
    document.getElementById("powerTable-download-button").style.display =
      "none";

    const alertDiv = document.getElementById("powerTable-alert");
    alertDiv.style.display = "block";
    alertDiv.textContent = "No components added";
  }

  /**
   * Handles the power switch event to toggle the visibility of the Power Table.
   * @param {Object} e - The event object containing details of the power switch event.
   * @param {boolean} e.enabled - Whether the Power Table is enabled (visible).
   */
  handlePowerSwitchEvent(e) {
    const isEnabled = e.enabled;
    this.modalDiv.style.display = isEnabled ? "flex" : "none";

    if (isEnabled) {
      // Correct position if modal is underneath the header
      this.correctModalPosition();
      // Initialize drag and resize functionality only if the modal is visible
      setTimeout(() => {
        this.initializeDragResize();
      }, 0);
      // Emit 'sceneUpdated' event to notify other parts of the application
      this.eventHub.emit("xmd");
    }
  }

  /**
   * Corrects the modal position if it's positioned underneath the header.
   * Ensures the modal is always accessible and not hidden behind the header.
   */
  correctModalPosition() {
    const header = document.getElementById("catHeader");
    if (header && this.modalDiv) {
      const headerRect = header.getBoundingClientRect();
      const modalRect = this.modalDiv.getBoundingClientRect();
      const minTop = headerRect.bottom;
      
      // If modal's top edge is below the header's bottom edge, reposition it
      if (modalRect.top < minTop) {
        this.modalDiv.style.top = `${minTop}px`;
      }
    }
  }

  /**
   * Initializes the drag and resize functionality for the Power Table modal.
   */
  initializeDragResize() {
    // Ensure initialization happens only once and modal is displayed
    if (!this.dragResizeModal && this.modalDiv.style.display !== "none") {
      // Find draggableContainer inside the modal
      const draggableContainer = this.modalDiv.querySelector(
        ".powerTable-draggable-container"
      );

      // Pass draggableContainer as the drag handle and modalDiv as the target
      this.dragResizeModal = new DragResizeModal(
        draggableContainer,
        this.modalDiv
      );
    }
  }

  /**
   * Updates the Power Table with the provided power data.
   * @param {Object} powerData - The data object containing power information.
   * @param {Array<Object>} powerData.data - Array of power data rows.
   */
  updatePowerTable(powerData) {
    const hasData = powerData && powerData.data.length > 0;

    this.setDownloadButtonVisibility(hasData);
    this.setAlertVisibility(hasData);

    if (hasData) {
      this.powerTableUI.updateTable(powerData);
      this.powerTableUI.updateFooter(this.calculateTotalPower(powerData.data));
    } else {
      this.powerTableUI.clearTable();
    }
  }

  /**
   * Calculates the total power from the provided power data.
   * @param {Array<Object>} data - Array of power data rows.
   * @returns {number} The total power value.
   */
  calculateTotalPower(data) {
    return data.reduce((sum, item) => sum + item.totalPower, 0);
  }

  /**
   * Sets the visibility of the download button.
   * @param {boolean} visible - Whether the download button should be visible.
   */
  setDownloadButtonVisibility(visible) {
    document.getElementById("powerTable-download-button").style.display =
      visible ? "block" : "none";
  }

  /**
   * Sets the visibility of the alert message for an empty Power Table.
   * @param {boolean} visible - Whether the alert should be visible.
   */
  setAlertVisibility(visible) {
    document.getElementById("powerTable-alert").style.display = visible
      ? "none"
      : "block";
  }
}
